import { Link } from 'react-router-dom';
import React, { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from './Header';
import { Footer } from './Footer';
import { RelatedLocations } from './RelatedLocations';
import { LocationFAQ } from './LocationFAQ';
import { SEO } from './SEO';
import { 
  Server, Zap, Shield, Globe, 
  CheckCircle, HardDrive, Network,
  DollarSign, MapPin, TrendingUp, Database,
  Rocket, Cloud, Users, ShoppingCart
} from 'lucide-react';
import { Button } from './ui/button';
import { PricingSection } from './PricingSection';
import { PaymentMethodsSection } from './PaymentMethodsSection';
import { smoothScrollToWithOffset } from '../lib/scroll';

export const PolandPage = () => {
  const { t, language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const stats = [
    { icon: DollarSign, value: t('poland.stats.price'), label: t('poland.stats.priceLabel') },
    { icon: Zap, value: t('poland.stats.speed'), label: t('poland.stats.speedLabel'), wiki: 'shared-1gbps-port-explained' },
    { icon: TrendingUp, value: t('poland.stats.uptime'), label: t('poland.stats.uptimeLabel') },
    { icon: MapPin, value: t('poland.stats.location'), label: t('poland.stats.locationLabel') }
  ];

  const costEffective = [
    {
      icon: DollarSign,
      title: t('poland.costEffective.value.title'),
      description: t('poland.costEffective.value.desc')
    },
    {
      icon: Shield,
      title: t('poland.costEffective.quality.title'),
      description: t('poland.costEffective.quality.desc')
    },
    {
      icon: Globe,
      title: t('poland.costEffective.market.title'),
      description: t('poland.costEffective.market.desc')
    },
    {
      icon: Rocket,
      title: t('poland.costEffective.startup.title'),
      description: t('poland.costEffective.startup.desc')
    }
  ];

  const techHub = [
    {
      icon: TrendingUp,
      title: t('poland.techHub.itCenter.title'),
      description: t('poland.techHub.itCenter.desc')
    },
    {
      icon: Network,
      title: t('poland.techHub.connectivity.title'),
      description: t('poland.techHub.connectivity.desc')
    },
    {
      icon: Users,
      title: t('poland.techHub.talent.title'),
      description: t('poland.techHub.talent.desc')
    },
    {
      icon: Server,
      title: t('poland.techHub.infrastructure.title'),
      description: t('poland.techHub.infrastructure.desc')
    }
  ];

  const startups = [
    {
      icon: Rocket,
      title: t('poland.startups.mvp.title'),
      description: t('poland.startups.mvp.desc')
    },
    {
      icon: Cloud,
      title: t('poland.startups.saas.title'),
      description: t('poland.startups.saas.desc')
    },
    {
      icon: Users,
      title: t('poland.startups.agencies.title'),
      description: t('poland.startups.agencies.desc')
    },
    {
      icon: ShoppingCart,
      title: t('poland.startups.ecommerce.title'),
      description: t('poland.startups.ecommerce.desc')
    }
  ];

  const scrollToPricing = () => smoothScrollToWithOffset('pricing', 100);

  return (
    <div className="min-h-screen bg-white">
      <SEO page="poland" />
      <Header />
      
      {/* Hero Section - Polish Red theme */}
      <section className="relative py-20 bg-gradient-to-br from-red-600 via-red-700 to-red-800 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-red-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-red-600 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-400/30 rounded-full text-red-50 text-sm font-semibold mb-6">
              <MapPin className="w-4 h-4" />
              <span>{t('poland.hero.badge')}</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {t('poland.hero.title')}
            </h1>
            <p className="text-xl text-red-50 max-w-3xl mx-auto mb-8">
              {t('poland.hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={scrollToPricing}
                className="bg-white text-red-700 hover:bg-red-50 font-semibold px-8 py-6 text-lg"
              >
                {t('poland.hero.getStarted')}
              </Button>
              <Button 
                onClick={scrollToPricing}
                className="bg-red-900 text-white hover:bg-red-950 font-semibold px-8 py-6 text-lg"
              >
                {t('poland.hero.viewPlans')}
              </Button>
            </div>
          </div>
          
          {/* Gdansk Old Town Image */}
          <div className="rounded-2xl overflow-hidden max-w-4xl mx-auto shadow-2xl">
            <img src="/images/locations/unsplash_1682261897890-7149592dc5c4.jpg" 
              alt="Gdansk Old City - Colorful Historic Buildings" loading="lazy"
              className="w-full h-96 object-cover"
            />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-xl mb-4">
                  <stat.icon className="w-8 h-8 text-red-600" />
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
                {stat.wiki && (
                  <Link
                    to={(language === 'ru' ? '/ru' : '') + '/wiki/' + stat.wiki}
                    className="mt-2 inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 transition-colors"
                  >
                    {language === 'ru' ? 'Что значит общий 1 Гбит/с?' : 'What does shared 1 Gbps mean?'} →
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Effective Section */}
      <section className="py-16 bg-gradient-to-b from-red-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('poland.costEffective.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('poland.costEffective.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {costEffective.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-8 border-2 border-red-200 hover:border-red-500 hover:shadow-lg transition-all">
                <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <item.icon className="w-7 h-7 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Hub Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('poland.techHub.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('poland.techHub.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {techHub.map((item, index) => (
              <div key={index} className="text-center p-6 bg-gradient-to-b from-red-50 to-white rounded-xl border border-red-200">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-xl mb-4">
                  <item.icon className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>      {/* Related Locations - Internal Links for SEO */}
      <RelatedLocations currentLocation="vps-poland" />



      {/* Pricing Section */}
      <section id="pricing" className="pt-16 pb-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('poland.pricing.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t('poland.pricing.subtitle')}
            </p>
          </div>
        </div>
        <PricingSection hideHeader={true} />
        
        {/* Payment Methods Section */}
        <div className="pt-16">
          <PaymentMethodsSection transparent={true} />
        </div>
      </section>

      {/* Startups Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('poland.startups.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('poland.startups.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {startups.map((startup, index) => (
              <div key={index} className="bg-gradient-to-br from-red-50 to-white p-6 rounded-xl border-2 border-red-100 hover:border-red-400 hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <startup.icon className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{startup.title}</h3>
                <p className="text-gray-600 text-sm">{startup.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location Details */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('poland.location.title')}
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                {t('poland.location.subtitle')}
              </p>
              <p className="text-gray-700 mb-6 leading-relaxed">
                {t('poland.location.about')}
              </p>
              <ul className="space-y-3">
                {t('poland.location.benefits').map((benefit, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img src="/images/locations/unsplash_1762715461119-cbd8d82a0aee.jpg" 
                alt="Gdansk Modern Business District" loading="lazy"
                className="w-full h-96 object-cover"
 loading="lazy"/>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-red-600 via-red-700 to-red-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-red-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-600 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('poland.cta.title')}
          </h2>
          <p className="text-xl text-red-50 mb-8 max-w-2xl mx-auto">
            {t('poland.cta.subtitle')}
          </p>
          <Button 
            onClick={scrollToPricing}
            className="bg-white text-red-700 hover:bg-red-50 font-bold px-10 py-6 text-xl"
          >
            {t('poland.cta.button')}
          </Button>
        </div>
      </section>

      <LocationFAQ location="vps-poland" />
      <Footer />
    </div>
  );
};


